import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChefHat, Camera, X, ArrowLeft, Sparkles, Flame, Snowflake, Users, Minus, Plus, AlertTriangle, Lightbulb, Star, Leaf, Salad, Zap, Soup, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { compressImage } from "@/lib/mbImage";
import { isFavorite, addFavorite, removeFavorite } from "@/lib/mbStorage";
import { detectAllergens, getDietaryWarnings } from "@/lib/mbNutrition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtQty = (q) => {
  const n = Math.round(q * 10) / 10;
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
};

const PLURAL = {
  cucchiaio: "cucchiai", cucchiaino: "cucchiaini", tazza: "tazze", fetta: "fette",
  foglia: "foglie", pizzico: "pizzichi", bicchiere: "bicchieri", spicchio: "spicchi",
};

const formatIngredient = (ing, servings) => {
  const unit = (ing.unit || "").toLowerCase();
  if (unit.includes("q.b") || ing.quantity <= 0) return "q.b.";
  const scaled = ing.quantity * servings;
  const u = (ing.unit || "").trim();
  const plural = scaled === 1 || !u ? u : PLURAL[u.toLowerCase()] || u;
  return `${fmtQty(scaled)} ${plural}`.trim();
};

const RecipeCard = ({ recipe, testid, profile }) => {
  const [servings, setServings] = useState(1);
  const isCold = recipe.type === "fredda";
  const accent = isCold ? "#2b90ef" : "#f59e0b";
  const Icon = isCold ? Snowflake : Flame;

  const favId = `chef-${recipe.type}-${(recipe.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`;
  const [fav, setFav] = useState(() => isFavorite(favId));

  const toggleFav = () => {
    if (fav) {
      removeFavorite(favId);
      setFav(false);
      toast("Rimossa dai preferiti");
    } else {
      addFavorite({
        id: favId,
        dish_name: recipe.title,
        image: null,
        recipe: { title: recipe.title, ingredients: recipe.ingredients, steps: recipe.steps },
      });
      setFav(true);
      toast.success("Salvata tra i preferiti ⭐", { description: recipe.title });
    }
  };

  const hasRestrictions =
    (profile?.allergies || []).some((a) => a !== "Altro") || !!profile?.allergyOther || !!profile?.diet;
  const recipeWarnings = getDietaryWarnings(profile || {}, [recipe.title, ...recipe.ingredients.map((i) => i.name)]);
  const isSafe = hasRestrictions && recipeWarnings.length === 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden mb-4" data-testid={testid}>
      <div className="px-4 pt-4 pb-3" style={{ borderTop: `3px solid ${accent}` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <span className="text-[10px] uppercase tracking-wide font-bold flex items-center gap-1" style={{ color: accent }}>
              <Icon size={13} /> {isCold ? "Ricetta Fredda" : "Ricetta Calda"}
            </span>
            <h3 className="font-display text-lg font-bold text-slate-800 leading-tight mt-0.5" data-testid={`${testid}-title`}>{recipe.title}</h3>
            {isSafe && (
              <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`${testid}-safe`}>
                <ShieldCheck size={11} /> Sicura per il tuo profilo
              </span>
            )}
            {hasRestrictions && recipeWarnings.length > 0 && (
              <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid={`${testid}-unsafe`}>
                <AlertTriangle size={11} /> Verifica ingredienti
              </span>
            )}
          </div>
          <button
            onClick={toggleFav}
            data-testid={`${testid}-favorite`}
            aria-label="Salva tra i preferiti"
            className="shrink-0 w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center active:scale-90 transition-transform"
          >
            <Star size={20} className={fav ? "fill-[#f59e0b] text-[#f59e0b]" : "text-slate-300"} />
          </button>
        </div>

        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-2 my-3">
          <span className="text-[12px] font-semibold text-slate-600 flex items-center gap-1.5"><Users size={14} style={{ color: accent }} /> Persone</span>
          <div className="flex items-center gap-3">
            <button onClick={() => setServings((s) => Math.max(1, s - 1))} disabled={servings <= 1} data-testid={`${testid}-minus`} className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-600 flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40"><Minus size={14} /></button>
            <span className="text-base font-bold font-display text-slate-800 w-5 text-center" data-testid={`${testid}-servings`}>{servings}</span>
            <button onClick={() => setServings((s) => Math.min(20, s + 1))} data-testid={`${testid}-plus`} className="w-7 h-7 rounded-lg text-white flex items-center justify-center active:scale-90 transition-transform" style={{ backgroundColor: accent }}><Plus size={14} /></button>
          </div>
        </div>

        <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-1.5">Ingredienti</span>
        <div className="rounded-xl border border-slate-100 overflow-hidden mb-3">
          {recipe.ingredients.map((ing, i) => (
            <div key={`${ing.name}-${i}`} className={`flex items-center justify-between px-3 py-1.5 text-[13px] ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
              <span className="text-slate-700">{ing.name}</span>
              <span className="font-semibold text-slate-900 shrink-0 ml-3">{formatIngredient(ing, servings)}</span>
            </div>
          ))}
        </div>

        <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-1.5">Preparazione</span>
        <ol className="space-y-2">
          {recipe.steps.map((step, i) => (
            <li key={`step-${i}`} className="flex gap-2.5">
              <span className="shrink-0 w-5 h-5 rounded-full text-white font-bold text-[11px] flex items-center justify-center font-display" style={{ backgroundColor: accent }}>{i + 1}</span>
              <p className="text-[13px] text-slate-600 leading-relaxed">{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

const STYLES = [
  { v: "leggero", l: "Leggero", Icon: Salad },
  { v: "veloce", l: "Veloce", Icon: Zap },
  { v: "comfort", l: "Comfort", Icon: Soup },
];

export const ChefOggi = ({ profile, onBack }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [style, setStyle] = useState(() => {
    try {
      return localStorage.getItem("mb_chef_style") || "leggero";
    } catch {
      return "leggero";
    }
  });

  const chooseStyle = (v) => {
    setStyle(v);
    try {
      localStorage.setItem("mb_chef_style", v);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("mb_chef_style non salvato:", err);
    }
  };

  const allergies = [
    ...((profile.allergies || []).filter((a) => a !== "Altro")),
    ...(profile.allergyOther ? [profile.allergyOther] : []),
  ];

  const fridgeAllergens = data ? getDietaryWarnings(profile, data.detected || []) : [];

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file, 900, 0.7);
      setPreview(dataUrl);
      setData(null);
      generate(dataUrl);
    } catch {
      toast.error("Impossibile leggere l'immagine");
    }
  };

  const generate = async (imageArg) => {
    const image = imageArg || preview;
    if (!image || loading) return;
    setLoading(true);
    setData(null);
    try {
      const clean = image.split(",")[1];
      const { data: res } = await axios.post(`${API}/chef-fridge`, {
        image_base64: clean,
        allergies,
        diet: profile.diet || "",
        style,
      });
      setData(res);
      if (!res.recipes?.length) toast.error("Nessuna ricetta trovata", { description: "Riprova con una foto più chiara" });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      toast.error("Generazione non riuscita", { description: err?.response?.data?.detail || "Riprova con un'altra foto" });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setData(null);
    setLoading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div className="w-full" data-testid="screen-chef">
      <div className="flex items-center gap-3 mb-4 mt-1">
        <button onClick={onBack} data-testid="chef-back-btn" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center active:scale-90 transition-transform">
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        <div>
          <h2 className="font-display text-xl font-bold text-slate-800 flex items-center gap-2"><ChefHat size={20} className="text-[#f59e0b]" /> Chef Oggi</h2>
          <p className="text-slate-400 text-xs">Cosa cucino oggi?</p>
        </div>
      </div>

      {/* Suggerimento foto */}
      <div className="rounded-2xl bg-[#fff7ed] border border-[#fcd9a5] p-3.5 flex items-start gap-2.5 mb-4" data-testid="chef-photo-tip">
        <Lightbulb size={18} className="text-[#f59e0b] shrink-0 mt-0.5" />
        <p className="text-[11px] leading-relaxed text-[#92400e]">
          <b>Come fare la foto:</b> apri il frigo, accendi la luce e inquadra bene ripiani e avanzi. Avvicina gli ingredienti principali e assicurati che siano a fuoco e ben visibili.
        </p>
      </div>

      {/* Dieta attiva */}
      {profile.diet && (
        <div className="rounded-2xl bg-[#eef5f1] border border-[#d8e8df] p-3 flex items-start gap-2.5 mb-4" data-testid="chef-diet-note">
          <Leaf size={16} className="text-[#4f9e78] shrink-0 mt-0.5" />
          <p className="text-[11px] leading-relaxed text-[#2f5c46]">
            Ricette adattate alla tua dieta: <b>{profile.diet === "vegana" ? "Vegana 🌱" : "Vegetariana 🥗"}</b>. Lo Chef userà solo ingredienti conformi.
          </p>
        </div>
      )}

      {/* Avviso intolleranze */}
      {allergies.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5 mb-4" data-testid="chef-allergy-warning">
          <AlertTriangle size={16} className="text-[#f59e0b] shrink-0 mt-0.5" />
          <p className="text-[11px] leading-relaxed text-[#92400e]">
            Le ricette verranno adattate alle tue intolleranze salvate: <b>{allergies.join(", ")}</b>. Controlla comunque sempre gli ingredienti.
          </p>
        </div>
      )}

      {/* Selettore stile ricette */}
      <div className="mb-4" data-testid="chef-style-selector">
        <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-2">Stile delle ricette</span>
        <div className="grid grid-cols-3 gap-2">
          {STYLES.map(({ v, l, Icon }) => {
            const active = style === v;
            return (
              <button
                key={v}
                onClick={() => chooseStyle(v)}
                data-testid={`chef-style-${v}`}
                className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[12px] font-semibold transition-colors active:scale-95 ${
                  active ? "bg-[#eef5f1] border-[#4f9e78] text-[#2f5c46]" : "bg-white border-slate-200 text-slate-500 hover:border-[#4f9e78]"
                }`}
              >
                <Icon size={18} className={active ? "text-[#4f9e78]" : "text-slate-400"} />
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Foto */}
      <div className="w-full bg-white rounded-3xl p-5 shadow-[0_8px_24px_rgba(43,144,239,0.08)] border border-slate-100 mb-4">
        <div className="relative w-full h-40 rounded-2xl overflow-hidden mb-4 bg-slate-100 flex items-center justify-center">
          {preview ? (
            <>
              <img src={preview} alt="Frigo" className="w-full h-full object-cover" data-testid="chef-preview" />
              {loading && <div className="mb-scanline" />}
              {loading && <div className="absolute inset-0 bg-slate-900/20" />}
              {!loading && (
                <button onClick={reset} data-testid="chef-clear-btn" className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-90 transition-transform"><X size={14} /></button>
              )}
            </>
          ) : (
            <Camera size={38} className="text-slate-300" strokeWidth={1.5} />
          )}
        </div>

        {!preview && (
          <button
            type="button"
            onClick={openPicker}
            data-testid="chef-upload-label"
            className="w-full flex items-center justify-center gap-2 cursor-pointer py-3.5 bg-[#f59e0b] hover:bg-[#d97706] text-white rounded-xl font-semibold text-sm transition-colors active:scale-[0.98]"
          >
            <Camera size={18} /> Fotografa il Frigo
          </button>
        )}
        {preview && !loading && (
          <button onClick={() => generate()} data-testid="chef-regenerate-btn" className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#f59e0b] hover:bg-[#d97706] text-white rounded-xl font-semibold text-sm transition-colors active:scale-[0.98]">
            <Sparkles size={18} /> {data ? "Genera di nuovo" : "Genera Ricette"}
          </button>
        )}
        <input id="chef-upload" ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="chef-upload-input" />
      </div>

      {loading && (
        <div className="w-full p-5 bg-white rounded-3xl border border-amber-100" data-testid="chef-loading">
          <div className="text-sm text-[#f59e0b] font-semibold mb-3 text-center flex items-center justify-center gap-2">
            <Sparkles size={14} className="animate-pulse" /> Lo Chef sta pensando a 2 ricette...
          </div>
          <div className="h-4 mb-skeleton rounded-md mb-2.5" />
          <div className="h-4 mb-skeleton rounded-md mb-2.5 w-4/5" />
          <div className="h-4 mb-skeleton rounded-md w-2/3" />
        </div>
      )}

      {data && !loading && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="chef-results">
          {fridgeAllergens.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5 mb-3" data-testid="chef-detected-allergy-alert">
              <AlertTriangle size={16} className="text-[#f59e0b] shrink-0 mt-0.5" />
              <div className="text-[12px] leading-relaxed text-[#92400e]">
                <p className="font-bold mb-0.5">Attenzione, in base al tuo profilo:</p>
                <ul className="space-y-0.5">
                  {fridgeAllergens.map((w, i) => (
                    <li key={`chef-warn-${i}`}>• {w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {data.detected?.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-1.5">Ingredienti riconosciuti</span>
              <div className="flex flex-wrap gap-1.5" data-testid="chef-detected">
                {data.detected.map((ing, i) => (
                  <span key={`${ing}-${i}`} className="text-[11px] bg-slate-50 border border-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">{ing}</span>
                ))}
              </div>
            </div>
          )}
          {data.recipes.map((r, i) => (
            <RecipeCard key={`chef-recipe-${i}`} recipe={r} profile={profile} testid={`chef-recipe-${r.type === "fredda" ? "cold" : "hot"}`} />
          ))}
        </motion.div>
      )}
    </div>
  );
};
